import { BookingType, IProviderData } from "types/appointmentBooking";
import {
    AppointmentBookingClientModel,
    AvailabilityTimeModel,
    DiscountType,
    IdNameItemModel,
} from "types/generated/models";
import { StepId } from "./bookingSlice.types";

export interface LocalStorageBookingData {
    location?: {
        locationId: number;
        locationName: string;
    };
    service?: {
        serviceId: number;
        serviceName: string;
    };
    provider?: IProviderData;
    group?: {
        groupId: string;
        groupName: string;
    };
    availability?: {
        timeEntry: AvailabilityTimeModel;
        selectedProvider?: IdNameItemModel;
        date: string;
        displayText: string;
    };
    confirmation?: {
        notes: string;
        client?: AppointmentBookingClientModel;
    };
    currentStep?: StepId;
    timestamp?: number;
}

const CLIENT_BOOKING_PREFIX = "client_booking_session_";
const CLIENT_TIMESTAMP_PREFIX = "client_booking_timestamp_";
const CLASS_BOOKING_PREFIX = "class_booking_session_";
const CLASS_TIMESTAMP_PREFIX = "class_booking_timestamp_";
const EXPIRY_TIME_MINUTES = 20;

export function getStorageKey(clientPortalGuid: string, bookingType: BookingType): string {
    const prefix = bookingType === BookingType.CLIENT ? CLIENT_BOOKING_PREFIX : CLASS_BOOKING_PREFIX;
    return `${prefix}${clientPortalGuid}`;
}

export function getTimestampKey(clientPortalGuid: string, bookingType: BookingType): string {
    const prefix = bookingType === BookingType.CLIENT ? CLIENT_TIMESTAMP_PREFIX : CLASS_TIMESTAMP_PREFIX;
    return `${prefix}${clientPortalGuid}`;
}

export function isNewSession(clientPortalGuid: string, bookingType: BookingType): boolean {
    try {
        const timestampKey = getTimestampKey(clientPortalGuid, bookingType);
        const savedTimestamp = localStorage.getItem(timestampKey);

        if (!savedTimestamp) {
            return true;
        }

        const now = Date.now();
        const timestamp = Number.parseInt(savedTimestamp);
        const expiryTimeMs = EXPIRY_TIME_MINUTES * 60 * 1000;

        return now - timestamp >= expiryTimeMs;
    } catch {
        return true;
    }
}

export function setTimestamp(clientPortalGuid: string, bookingType: BookingType): void {
    try {
        const timestampKey = getTimestampKey(clientPortalGuid, bookingType);
        localStorage.setItem(timestampKey, Date.now().toString());
    } catch (error) {
        console.warn("Failed to set timestamp:", error);
    }
}

export function getSession(clientPortalGuid: string, bookingType: BookingType): LocalStorageBookingData | null {
    try {
        const storageKey = getStorageKey(clientPortalGuid, bookingType);
        const serializedData = localStorage.getItem(storageKey);

        if (!serializedData || serializedData === "") {
            return null;
        }

        const data = JSON.parse(serializedData) as LocalStorageBookingData;
        setTimestamp(clientPortalGuid, bookingType);
        return data;
    } catch (error) {
        console.warn("Failed to get session:", error);
        return null;
    }
}

export function saveBookingDataToLocalStorage(
    data: LocalStorageBookingData,
    clientPortalGuid: string,
    bookingType: BookingType
): void {
    try {
        const storageKey = getStorageKey(clientPortalGuid, bookingType);
        const dataWithTimestamp = { ...data, timestamp: Date.now() };
        const serializedData = JSON.stringify(dataWithTimestamp);
        localStorage.setItem(storageKey, serializedData);
        setTimestamp(clientPortalGuid, bookingType);
    } catch (error) {
        console.warn("Failed to save booking data to localStorage:", error);
    }
}

export function loadBookingDataFromLocalStorage(
    clientPortalGuid: string,
    bookingType: BookingType
): LocalStorageBookingData | null {
    try {
        if (isNewSession(clientPortalGuid, bookingType)) {
            setTimestamp(clientPortalGuid, bookingType);
            clearBookingDataFromLocalStorage(clientPortalGuid, bookingType);
            return null;
        }

        return getSession(clientPortalGuid, bookingType);
    } catch (error) {
        console.warn("Failed to load booking data from localStorage:", error);
        return null;
    }
}

export function clearBookingDataFromLocalStorage(clientPortalGuid: string, bookingType: BookingType): void {
    try {
        const storageKey = getStorageKey(clientPortalGuid, bookingType);
        const timestampKey = getTimestampKey(clientPortalGuid, bookingType);
        localStorage.removeItem(storageKey);
        localStorage.removeItem(timestampKey);
    } catch (error) {
        console.warn("Failed to clear booking data from localStorage:", error);
    }
}

export function clearAllBookingDataFromLocalStorage(clientPortalGuid: string): void {
    try {
        clearBookingDataFromLocalStorage(clientPortalGuid, BookingType.CLIENT);
        clearBookingDataFromLocalStorage(clientPortalGuid, BookingType.CLASS);
    } catch (error) {
        console.warn("Failed to clear all booking data from localStorage:", error);
    }
}

/**
 * Applies a client discount to an amount.
 * @param discount - Percentage as decimal (0.1 = 10%) or fixed currency amount
 */
export function applyDiscount(amount: number, discount?: number, discountType?: DiscountType): number {
    if (!discount || discount <= 0 || amount <= 0) {
        return amount;
    }

    if (discountType === DiscountType.Percentage) {
        // Percentage discount is stored as decimal (e.g., 0.1 for 10%)
        return Math.max(0, amount * (1 - discount));
    }

    return Math.max(0, amount - discount);
}

export function discountText(discount: number, discountType: DiscountType | undefined, currencySymbol: string): string {
    if (discountType === DiscountType.Percentage) {
        return `${parseFloat((discount * 100).toFixed(2))}%`;
    }
    return `${currencySymbol}${discount.toFixed(2)}`;
}
